import { currentCash } from "../../helpers/GameSelectors";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../Store";
import {
  pendingScenarioChoice,
  scenarioChoiceDescription,
} from "../../helpers/ScenarioChoices";
import { chooseScenarioResponse } from "../../reducers/GameActions";
import { formatMoneyConcise } from "../../helpers/Format";
import ConceptIcon from "./ConceptIcon";

export default function ScenarioChoiceDialog() {
  const game = useAppSelector((state) => state.game);
  const dispatch = useAppDispatch();
  const decision =
    game.inGame && !game.replayPlayback
      ? pendingScenarioChoice(game)
      : undefined;
  if (!decision) return null;
  const cash = currentCash(game);
  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      aria-labelledby="scenarioChoiceTitle"
      aria-describedby="scenarioChoiceDescription"
      data-scenario-choice="true"
      slotProps={{
        paper: {
          sx: {
            m: { xs: 1.5, sm: 4 },
            width: { xs: "calc(100% - 24px)", sm: "calc(100% - 64px)" },
            maxHeight: {
              xs: "calc(100dvh - 24px)",
              sm: "calc(100dvh - 64px)",
            },
          },
        },
      }}
    >
      <DialogTitle
        id="scenarioChoiceTitle"
        sx={{
          "&&": {
            px: { xs: 2, sm: 3 },
            pt: 2,
            pb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          },
        }}
      >
        <span style={{ flex: 1 }}>{decision.title}</span>
        <span className="pausedChip">
          <span aria-hidden="true">
            <ConceptIcon concept="pause" fontSize="small" />
          </span>
          Paused
        </span>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
        <Typography id="scenarioChoiceDescription" sx={{ mb: 2 }}>
          {decision.message}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          {decision.options.map((option, index) => {
            const cost = option.cost(game.difficulty);
            const affordable = cost === 0 || cash >= cost;
            return (
              <Box key={option.id}>
                <Button
                  fullWidth
                  autoFocus={index === 0 && affordable}
                  variant={index === 0 ? "contained" : "outlined"}
                  disabled={!affordable}
                  aria-describedby={`scenarioChoiceOption-${option.id}`}
                  sx={{ minHeight: 44, whiteSpace: "normal" }}
                  onClick={() =>
                    dispatch(
                      chooseScenarioResponse({
                        decisionId: decision.id,
                        optionId: option.id,
                      }),
                    )
                  }
                >
                  {cost > 0
                    ? `${option.label} (${formatMoneyConcise(cost)})`
                    : option.label}
                </Button>
                <Typography
                  id={`scenarioChoiceOption-${option.id}`}
                  variant="body2"
                  sx={{ color: "text.secondary", mt: 0.5 }}
                >
                  {scenarioChoiceDescription(option, game.difficulty)}
                  {!affordable && " · Insufficient cash"}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
